import React from 'react';
import { CheckCircle, AlertCircle } from 'lucide-react';
import type { TaskSignatureResponse } from '../../services/api/types.gen';
import { signerName } from './signatureSummary';
import {
  formatSignatureDate,
  formatCeremonyTimestamp,
} from './signatureDisplayDate';

interface SignatureCardProps {
  signature: TaskSignatureResponse;
  /** Optional emphasis style (e.g. inside a highlighted banner). */
  tone?: 'default' | 'amber';
  /**
   * Also show when the signature was actually recorded, whenever that differs
   * from the date the signer declared.
   *
   * Set on the audit trail. Everywhere else shows the declared date alone,
   * because that is what the clinician attested to — but an audit that showed
   * only the declared date would hide the very thing it exists to reveal: that
   * the chart was signed on a different day from the one printed on it.
   */
  showRecordedAt?: boolean;
}

/**
 * Renders an "Electronically Signed By" block for a single task signature.
 *
 * Intentionally mirrors what goes into the CMS-485 PDF so on-screen and
 * printed rendering agree: printed name + credentials, the signature date, and
 * a truncated signature id as provenance.
 *
 * The date line shows what the signer declared, falling back to the ceremony
 * instant in the viewer's own timezone. Pass `showRecordedAt` in an audit
 * context to add the ceremony instant in UTC beneath it — the printed artifact
 * carries no clock at all, so this dialog is the only place the exact moment
 * is readable.
 */
export const SignatureCard: React.FC<SignatureCardProps> = ({
  signature,
  showRecordedAt = false,
  tone = 'default',
}) => {
  const metadata =
    (signature.signature_metadata as
      | Record<string, unknown>
      | null
      | undefined) ?? {};
  const displayName = signerName(signature);
  const credentials = metadata.signer_credentials as string | undefined | null;
  const role = metadata.signer_role as string | undefined;
  const invalidated = !!signature.invalidated_at;
  const hasIntegrity = !!signature.content_hash_hex;

  const nameLine = credentials ? `${displayName}, ${credentials}` : displayName;

  const isAmber = tone === 'amber';
  const container = isAmber
    ? 'bg-amber-50/60 border-amber-200'
    : 'bg-muted/30 border-border/70';
  const subtle = isAmber ? 'text-amber-700' : 'text-muted-foreground';
  const strong = isAmber ? 'text-amber-900' : 'text-foreground';

  return (
    <div className={`rounded-md border ${container} px-3 py-2.5`}>
      <div className='flex items-start gap-2'>
        {invalidated ? (
          <AlertCircle className='mt-0.5 h-4 w-4 flex-shrink-0 text-destructive' />
        ) : (
          <CheckCircle className='mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600' />
        )}
        <div className='flex-1 min-w-0'>
          <div className={`text-[11px] uppercase tracking-wide ${subtle}`}>
            Electronically Signed By
            {role ? ` · ${role.replace(/_/g, ' ')}` : ''}
          </div>
          <div
            className={`text-sm font-medium ${strong} ${
              invalidated ? 'line-through' : ''
            }`}
          >
            {nameLine}
          </div>
          <div className={`text-xs ${subtle}`}>
            {formatSignatureDate(signature)}
          </div>
          {/* Ungated on signature_date: this is the audit trail's fixed
              anchor. The line above renders in the VIEWER's timezone when no
              date was declared, so suppressing this one exactly then left two
              auditors reading different calendar days with nothing on screen
              saying which clock either was on. */}
          {showRecordedAt ? (
            <div className={`text-[11px] ${subtle}`}>
              Recorded {formatCeremonyTimestamp(signature.signed_at)}
            </div>
          ) : null}
          <div className={`text-[11px] font-mono ${subtle}`}>
            Signature ID: {signature.id.slice(0, 8)}
            {hasIntegrity ? ' · hmac-sha256' : ''}
          </div>
          {invalidated ? (
            <div className='mt-1 text-[11px] text-destructive'>
              Invalidated
              {signature.invalidation_reason
                ? ` — ${signature.invalidation_reason}`
                : ''}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
